import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
  Grid,
  FormControlLabel,
  Switch,
  Typography,
  Tabs,
  Tab
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { uploadImage } from '../utils/imageUpload';

const EditBannerDialog = ({ open, onClose, banner, onSave }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  
  const [formData, setFormData] = useState({
    image_en: '',
    image_fin: '',
    image_fr: '',
    image_de: '',
    alt_text_en: '',
    alt_text_fin: '',
    alt_text_fr: '',
    alt_text_de: '',
    rank: 1,
    status: 1
  });

  const [selectedFiles, setSelectedFiles] = useState({
    en: null,
    fin: null,
    fr: null,
    de: null
  });

  const languages = [
    { code: 'en', label: 'English' },
    { code: 'fin', label: 'Finnish' },
    { code: 'fr', label: 'French' },
    { code: 'de', label: 'German' }
  ];

  useEffect(() => {
    if (banner) {
      setFormData({
        image_en: banner.image_en || '',
        image_fin: banner.image_fin || '',
        image_fr: banner.image_fr || '',
        image_de: banner.image_de || '',
        alt_text_en: banner.alt_text_en || '',
        alt_text_fin: banner.alt_text_fin || '',
        alt_text_fr: banner.alt_text_fr || '',
        alt_text_de: banner.alt_text_de || '',
        rank: parseInt(banner.rank) || 1,
        status: parseInt(banner.status) || 0
      });
      setError('');
      setSuccess(false);
      setSelectedFiles({
        en: null,
        fin: null,
        fr: null,
        de: null
      });
    }
  }, [banner]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileSelect = (lang, event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFiles(prev => ({
        ...prev,
        [lang]: file
      }));
      
      // Create preview URL
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({
          ...prev,
          [`image_${lang}`]: reader.result
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async () => {
    if (!banner) return;

    console.log('Banner object:', banner);
    console.log('Banner keys:', Object.keys(banner));

    // Validation
    if (!formData.rank || formData.rank < 1) {
      setError('Rank must be at least 1');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Upload new files first
      const uploadedUrls = { ...formData };
      
      for (const lang of ['en', 'fin', 'fr', 'de']) {
        if (selectedFiles[lang]) {
          try {
            const uploadedUrl = await uploadImage(selectedFiles[lang]);
            uploadedUrls[`image_${lang}`] = uploadedUrl;
          } catch (uploadError) {
            setError(`Failed to upload ${lang.toUpperCase()} image`);
            setLoading(false);
            return;
          }
        }
      }

      // Ensure image_en has a value (use existing if no new upload)
      if (!uploadedUrls.image_en) {
        setError('English image is required');
        setLoading(false);
        return;
      }

      const payload = {
        banner_id: parseInt(banner.banner_id),
        image_en: uploadedUrls.image_en,
        image_fin: uploadedUrls.image_fin || '',
        image_fr: uploadedUrls.image_fr || '',
        image_de: uploadedUrls.image_de || '',
        alt_text_en: formData.alt_text_en || '',
        alt_text_fin: formData.alt_text_fin || '',
        alt_text_fr: formData.alt_text_fr || '',
        alt_text_de: formData.alt_text_de || '',
        rank: parseInt(formData.rank),
        status: formData.status
      };

      console.log('Sending payload:', payload);
      console.log('Banner ID:', banner.banner_id, 'Parsed:', parseInt(banner.banner_id));
      console.log('Image EN:', uploadedUrls.image_en);
      console.log('Rank:', formData.rank, 'Parsed:', parseInt(formData.rank));

      const response = await fetch('https://elnaf.fi/apiElnaf/api/update_banner.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      console.log('Response status:', response.status);
      const responseText = await response.text();
      console.log('Response text:', responseText);
      
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (e) {
        console.error('Failed to parse JSON:', e);
        setError('Invalid response from server');
        setLoading(false);
        return;
      }

      console.log('Data received:', data);
      console.log('onSave function:', onSave);
      
      // Check for success - backend might return Turkish message
      if (response.ok && (data.status === 'success' || data.message === 'Banner uğurla yeniləndi.')) {
        setSuccess(true);
        console.log('Success detected! Will close in 1.5 seconds');
        // Show success for 1.5 seconds then close
        setTimeout(() => {
          console.log('Timeout reached, calling onSave');
          if (onSave) {
            console.log('Calling onSave now');
            onSave(); // This will refresh and close
          } else {
            console.log('onSave is undefined!');
          }
        }, 1500);
      } else {
        setError(data.message || 'Failed to update banner');
      }
    } catch (err) {
      console.error('Error:', err);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading && !success) {
      setError('');
      setSuccess(false);
      setActiveTab(0);
      onClose();
    }
  };

  if (!banner) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Edit Banner
        <Box component="span" sx={{ display: 'block', fontSize: '14px', color: 'text.secondary', mt: 1 }}>
          ID: {banner.banner_id}
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Banner updated successfully!
            </Alert>
          )}

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Rank (Order)"
                name="rank"
                type="number"
                value={formData.rank}
                onChange={handleChange}
                InputProps={{ inputProps: { min: 1 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.status === 1}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      status: e.target.checked ? 1 : 0
                    }))}
                  />
                }
                label={formData.status === 1 ? 'Active' : 'Inactive'}
              />
            </Grid>
          </Grid>

          <Tabs value={activeTab} onChange={handleTabChange} sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
            {languages.map((lang, index) => (
              <Tab key={lang.code} label={lang.label} />
            ))}
          </Tabs>

          {languages.map((lang, index) => (
            <Box
              key={lang.code}
              hidden={activeTab !== index}
              sx={{ pt: 2 }}
            >
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  {lang.code === 'en' && (
                    <Typography variant="caption" color="error" display="block" sx={{ mb: 1 }}>
                      * Required
                    </Typography>
                  )}
                  {formData[`image_${lang.code}`] && !selectedFiles[lang.code] && (
                    <Box sx={{ mb: 2, textAlign: 'center' }}>
                      <Typography variant="caption" display="block" sx={{ mb: 1 }}>
                        Current Image:
                      </Typography>
                      <img
                        src={formData[`image_${lang.code}`]}
                        alt={`Current ${lang.label}`}
                        style={{ maxWidth: '100%', maxHeight: '150px' }}
                      />
                    </Box>
                  )}
                  
                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={<CloudUploadIcon />}
                    fullWidth
                  >
                    {formData[`image_${lang.code}`] ? 'Change' : 'Upload'} {lang.label} Image
                    <input
                      type="file"
                      hidden
                      accept="image/*"
                      onChange={(e) => handleFileSelect(lang.code, e)}
                    />
                  </Button>
                  
                  {selectedFiles[lang.code] && (
                    <>
                      <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                        New file: {selectedFiles[lang.code].name}
                      </Typography>
                      <Box sx={{ mt: 2, textAlign: 'center' }}>
                        <img
                          src={formData[`image_${lang.code}`]}
                          alt={`Preview ${lang.label}`}
                          style={{ maxWidth: '100%', maxHeight: '150px' }}
                        />
                      </Box>
                    </>
                  )}
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label={`Alt Text (${lang.label})`}
                    name={`alt_text_${lang.code}`}
                    value={formData[`alt_text_${lang.code}`]}
                    onChange={handleChange}
                    multiline
                    rows={2}
                  />
                </Grid>
              </Grid>
            </Box>
          ))}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading || success}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || success}
          startIcon={loading && <CircularProgress size={20} />}
        >
          {success ? 'Saved!' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditBannerDialog;